import { jwtDecode } from 'jwt-decode';

import { LoginId } from '@suntex.fim/api-client';
import { LoginType } from '@suntex.fim/enums';

import { BasicLoginInfo } from './types';

/**
 * The claims encoded in JWTs issued by the Suntex backend.
 * Claim names are camelCased as produced by JwtService.cs.
 */
export interface JwtClaims {
  loginType: string | number;
  loginId: number;
  userId: string;
  /** Expiry as a Unix timestamp (seconds since epoch) */
  exp?: number;
  iat?: number;
}

const SECONDS_PER_MS = 1000;

/**
 * Decode the claims payload of a JWT without verifying the signature.
 * Verification is always server-side; this is for reading claims client-side only.
 */
export function decodeJwtClaims(jwt: string): JwtClaims {
  return jwtDecode<JwtClaims>(jwt);
}

/**
 * Returns the token expiry as a Unix timestamp in milliseconds, or null if no
 * `exp` claim is present.
 */
export function getTokenExpiryMs(jwt: string): number | null {
  const claims = decodeJwtClaims(jwt);
  return claims.exp != null ? claims.exp * SECONDS_PER_MS : null;
}

/**
 * Returns true if the JWT will expire within `thresholdMs` milliseconds.
 */
export function isTokenExpiringSoon(jwt: string, thresholdMs: number): boolean {
  const expiryMs = getTokenExpiryMs(jwt);
  if (expiryMs == null) return false;
  return expiryMs - Date.now() < thresholdMs;
}

/**
 * Returns true if the JWT's `exp` claim is in the past.
 * Returns false (not expired) for tokens that cannot be decoded.
 */
export function isTokenExpired(jwt: string): boolean {
  try {
    const expiryMs = getTokenExpiryMs(jwt);
    return expiryMs != null && Date.now() >= expiryMs;
  } catch {
    return false;
  }
}

/**
 * Extract `loginType` and `loginId` from the JWT claims so that
 * `BasicLoginInfo` can be obtained from a raw JWT without a separate API call.
 */
export function extractLoginInfo(jwt: string): BasicLoginInfo {
  const claims = decodeJwtClaims(jwt);
  const loginTypeValue =
    typeof claims.loginType === 'string'
      ? LoginType[claims.loginType as keyof typeof LoginType]
      : claims.loginType;

  if (!Object.values(LoginType).includes(loginTypeValue)) {
    throw new Error(
      `JWT contains unknown loginType claim value: ${claims.loginType}`,
    );
  }

  return {
    loginType: loginTypeValue,
    loginId: claims.loginId as LoginId,
  };
}
